import { useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AccountMenu } from './AccountMenu';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Footer } from './Footer';
import { BottomNav } from './BottomNav';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { useStoreSettings } from '@/features/settings/hooks/useStoreSettings';
import { SessionExpiryWarning } from '@/components/common/SessionExpiryWarning';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';
import { parkCartForRecovery } from '@/lib/utils/session';
import { ROLES } from '@/lib/config/constants';
import { ROUTES } from '@/lib/config/routes';

export function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useIdleLogout(true);

  const role = useAuthStore((s) => s.user?.role);

  if (role === ROLES.SALES) return <SalesOnlyShell />;

  return (
    <>
      <SessionExpiryWarning />
      <TenantShell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
    </>
  );
}

function TenantShell({
  sidebarOpen,
  setSidebarOpen,
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (v: boolean) => void;
}) {
  useRoles();
  useStoreSettings();

  return (
    <div className="flex h-screen flex-col bg-surface">
      <div className="flex min-h-0 flex-1">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 pb-24 md:p-6 md:pb-24 lg:pb-6">
            <Outlet />
          </main>
          <div className="hidden lg:block">
            <Footer />
          </div>
        </div>
      </div>
      <BottomNav onMore={() => setSidebarOpen(true)} />
    </div>
  );
}

function SalesOnlyShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);

  if (location.pathname !== ROUTES.pos) {
    return <Navigate to={ROUTES.pos} replace />;
  }

  const onLogout = () => {
    parkCartForRecovery();
    clearSession();
    useCartStore.getState().clear();
    navigate(ROUTES.login, { replace: true });
  };

  return (
    <div className="flex h-screen flex-col bg-surface">
      <header className="relative z-30 flex h-16 shrink-0 items-center justify-between border-b border-border bg-surface-elevated px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(ROUTES.pos)}
            className="flex items-center gap-2 rounded-lg transition hover:opacity-80"
          >
            <img
              src="/images/logos/logo.png"
              alt="DB Scaffolding"
              className="h-7 w-auto object-contain dark:brightness-0 dark:invert"
            />
            <span className="text-sm font-bold text-content">DB Scaffolding</span>
          </button>
        </div>
        <AccountMenu
          user={user}
          showAccount={false}
          onAccount={() => {}}
          onLogout={onLogout}
          singleInitial
        />
      </header>
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
